import PropTypes from "prop-types";
import { useCallback, useRef } from "react";
import SignType from "../../../../domain/signType";
import { usd } from "../../../../util/formatCurrency.js";
import {
  cannotBePurchased,
  isVaulted as isProductVaulted,
} from "./productAdapter";
import s from "./VariantPicker.module.css";
import signTypeColor from "../../../../components/SignTypeChip/signTypeColor";

/** Customer-facing experience description + estimated wait per signing type. */
const SIGNING_INFO = {
  [SignType.livestream]: {
    experience:
      "Watch your item get signed live on stream. Ships after signing event",
  },
  [SignType.anytime]: {
    experience: "Get a video of your item being signed, sent to your email",
  },
  [SignType.presigned]: {
    experience: "Pre-signed, ships fast!",
  },
  [SignType.presignedWithVideo]: {
    experience:
      "Pre-signed item plus a personalized video to your email. Ships fast!",
  },
  [SignType.digital]: {
    experience: "Digitally signed, ships fast!",
  },
  [SignType.offCamera]: {
    experience: "Your item will be signed off camera",
  },
  [SignType.noAutograph]: {
    experience: "No autograph \u2014 just the item itself",
  },
  [SignType.shoutout]: {
    experience:
      "A personalized video shoutout sent to your email. Delivered via email",
  },
  [SignType.audioShoutout]: {
    experience:
      "A personalized audio message sent to your email. Delivered via email",
  },
  [SignType.inPerson]: {
    experience: "Receive your signed item at the event",
  },
};

VariantPicker.propTypes = {
  variants: PropTypes.arrayOf(
    PropTypes.shape({
      product_id: PropTypes.number.isRequired,
      signed_type: PropTypes.string,
      _totalPrice: PropTypes.number,
      stock: PropTypes.number,
      vaulted_until: PropTypes.string,
    }),
  ),
  selectedVariant: PropTypes.object,
  onSelect: PropTypes.func.isRequired,
};

export default function VariantPicker({ variants, selectedVariant, onSelect }) {
  const groupRef = useRef(null);

  const handleKeyDown = useCallback(
    (e) => {
      if (
        !["ArrowDown", "ArrowUp", "ArrowRight", "ArrowLeft"].includes(e.key)
      ) {
        return;
      }

      e.preventDefault();
      const buttons = groupRef.current?.querySelectorAll(
        "button[role='radio']",
      );
      if (!buttons?.length) return;

      const currentIdx = Array.from(buttons).findIndex(
        (b) => b === document.activeElement,
      );
      let nextIdx;
      if (e.key === "ArrowDown" || e.key === "ArrowRight") {
        nextIdx = currentIdx < buttons.length - 1 ? currentIdx + 1 : 0;
      } else {
        nextIdx = currentIdx > 0 ? currentIdx - 1 : buttons.length - 1;
      }

      buttons[nextIdx].focus();
      // Find the variant for this button and select it
      const pid = Number(buttons[nextIdx].dataset.pid);
      const v = variants.find((vr) => vr.product_id === pid);
      if (v) onSelect(v);
    },
    [variants, onSelect],
  );

  if (!Array.isArray(variants) || variants.length <= 1) return null;

  return (
    <section
      className={s.signingSection}
      aria-labelledby="signing-option-title"
    >
      <div className={s.signingSectionHeader}>
        <h3 id="signing-option-title" className={s.signingSectionTitle}>
          Choose Your Signing Option
        </h3>
        <p className={s.signingSectionSubtitle}>
          Select how you&apos;d like your item signed
        </p>
      </div>

      <div
        ref={groupRef}
        role="radiogroup"
        aria-label="Signing options"
        onKeyDown={handleKeyDown}
      >
        {variants.map((v) => {
          const selected = selectedVariant?.product_id === v.product_id;
          const unavailable = cannotBePurchased(v);
          const isVaulted = isProductVaulted(v);
          const info = SIGNING_INFO[v.signed_type];

          return (
            <button
              key={v.product_id}
              type="button"
              role="radio"
              aria-checked={selected}
              data-pid={v.product_id}
              tabIndex={
                selected || (!selectedVariant && v === variants[0]) ? 0 : -1
              }
              className={`${s.signingOption} ${
                selected ? s.signingOptionSelected : ""
              } ${unavailable ? s.signingOptionUnavailable : ""}`}
              onClick={() => onSelect(v)}
            >
              <span className={s.signingRadio}>
                <span className={s.signingRadioInner} />
              </span>
              <span className={s.signingInfo}>
                <p className={s.signingType}>
                  <span
                    className={s.signingDot}
                    style={{
                      backgroundColor:
                        signTypeColor(v.signed_type) || "var(--md-primary)",
                    }}
                    aria-hidden="true"
                  />
                  {SignType.toLabel(v.signed_type)} &middot; {v.product_type}
                </p>
                {info?.experience && (
                  <p className={s.signingDesc}>{info.experience}</p>
                )}
                {unavailable && (
                  <p className={s.signingUnavailable}>
                    {isVaulted ? "In the Vault" : "Sold out"}
                  </p>
                )}
              </span>
              <span className={s.signingPrice}>{usd(v._totalPrice)}</span>
            </button>
          );
        })}
      </div>
    </section>
  );
}
